import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { MaskLines, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const CELogo = () => (
  <svg viewBox="0 0 120 80" className="h-14 w-auto" aria-label="Marcatura CE">
    <path d="M50 12a28 28 0 1 0 0 56" fill="none" stroke="currentColor" strokeWidth="9" />
    <path d="M108 12a28 28 0 1 0 0 56M80 40h24" fill="none" stroke="currentColor" strokeWidth="9" />
  </svg>
);

const TextBadge = ({ top, main, bottom }) => {
  const t = useT();
  return (
  <div className="flex h-14 items-center gap-3">
    <span className="grid h-14 w-14 place-items-center border-2 border-current font-display text-lg font-extrabold" style={{ fontStretch: "80%" }}>{main}</span>
    <span className="font-mono text-xs uppercase leading-tight tracking-[0.15em]">{t(`badge.${top}`, top)}<br />{t(`badge.${bottom}`, bottom)}</span>
  </div>
  );
};

const CERTS = [
  {
    id: "ce",
    logo: <CELogo />,
    title: "Marcatura CE – Legno lamellare incollato",
    meta: "Certificato 0407-CPR-1459 · organismo notificato n. 0407",
    text: "Attesta che il nostro legno lamellare è prodotto con controllo di fabbrica verificato da un ente terzo, secondo il sistema 1 del Regolamento UE 305/2011: prestazioni costanti, lotto dopo lotto.",
    files: [["Scarica PDF", "/docs/certificato-costanza-prestazione-0407-CPR-1459.pdf"]],
  },
  {
    id: "dop",
    logo: <TextBadge main="DoP" top="EN 14080" bottom="Reg. UE 305/2011" />,
    title: "Dichiarazione di prestazione",
    meta: "Norma armonizzata EN 14080:2013 · uso previsto: edifici e ponti",
    text: "Dichiara le caratteristiche tecniche del prodotto (resistenza, reazione al fuoco, durabilità). È il documento che il progettista usa per calcolare e verificare la struttura.",
    files: [["Scarica PDF", "/docs/dichiarazione-di-prestazione-EN14080.pdf"]],
  },
  {
    id: "stc",
    logo: <TextBadge main="STC" top="NTC 2018" bottom="Centro di lavorazione" />,
    title: "Centro di lavorazione elementi strutturali",
    meta: "Attestato n. 22/22-CL · Servizio Tecnico Centrale, Consiglio Superiore LL.PP.",
    text: "Riconosce il nostro stabilimento come centro autorizzato alla lavorazione di legno massiccio, lamellare e pannelli per uso strutturale, come previsto dal DM 17 gennaio 2018.",
    files: [["Scarica PDF", "/docs/attestato-centro-lavorazione-22-22-CL.pdf"]],
  },
  {
    id: "en1090",
    logo: <TextBadge main="1090" top="EN 1090-1" bottom="Strutture in acciaio" />,
    title: "EN 1090 – Carpenteria metallica",
    meta: "[Ente certificatore] · [n. certificato] · classe di esecuzione [EXC]",
    text: "Abilita la marcatura CE dei componenti strutturali in acciaio: saldature, processi e controlli di produzione sono verificati da un organismo notificato. Garantisce connessioni e piastre sicure per le nostre strutture in legno.",
    files: [],
  },
  {
    id: "pefc",
    logo: <TextBadge main="PEFC" top="Catena di custodia" bottom="Foreste gestite" />,
    title: "PEFC – Legno da foreste sostenibili",
    meta: "Certificato di gruppo CSQA-PEFC-COC-54966 · CSQA Certificazioni · valido fino al 16/12/2028",
    text: "Garantisce che il legno che lavoriamo proviene da foreste gestite in modo sostenibile e ne traccia l'origine lungo tutta la filiera, dalla foresta alla struttura consegnata.",
    files: [["Scarica PDF", "/docs/certificato-PEFC-CSQA-COC-54966.pdf"]],
  },
  {
    id: "iso9001",
    logo: <TextBadge main="ISO" top="9001:2015" bottom="Sistema qualità" />,
    title: "ISO 9001 – Gestione della qualità",
    meta: "Certificato n. 3919760 · LL-C Certification · valido fino al 23/10/2027",
    text: "Certifica che il sistema di gestione per la qualità di Emma Industrie – lavorazione di legno e ferro, produzione di elementi in legno incollato – segue processi controllati e orientati al miglioramento continuo.",
    files: [["PDF italiano", "/docs/certificato-ISO-9001-3919760-IT.pdf"], ["PDF English", "/docs/certificato-ISO-9001-3919760-EN.pdf"]],
  },
];

export const Certifications = () => {
  const t = useT();
  return (
  <section id="certificazioni" className="grain relative bg-[#121110] px-5 py-28 text-[#F7F5F0] sm:px-8 lg:py-40" data-testid="certifications-section">
    <div className="mx-auto max-w-[1500px]">
      <SectionLabel index="08" dark>{t("cert.label", "Certificazioni")}</SectionLabel>
      <MaskLines
        className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
        lines={[t("cert.h1", "Qualità dichiarata,"), <span key="c"><em className="font-serif font-normal text-[#D98A52]">{t("cert.h2em", "verificata")}</em>, {t("cert.h2b", "firmata.")}</span>]}
      />
      <p className="mt-6 max-w-2xl text-sm leading-relaxed text-[#A39E93]">{t("cert.note", "I documenti scaricabili sono copie informative con filigrana anticontraffazione. Per una copia conforme scrivi a info@emmaindustrie.it.")}</p>
      <div className="mt-16 grid gap-4 md:grid-cols-3">
        {CERTS.map((c, i) => (
          <motion.article
            key={c.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="group flex flex-col border border-white/10 bg-[#1B1816] p-8 transition-[border-color,background-color] duration-500 hover:border-[#D98A52]/60 hover:bg-[#211D1A]"
            data-testid={`cert-card-${c.id}`}
          >
            <div className="text-[#F7F5F0] transition-colors duration-500 group-hover:text-[#D98A52]">{c.logo}</div>
            <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight">{t(`cert.${c.id}.title`, c.title)}</h3>
            <p className="mt-2 font-mono text-xs uppercase leading-relaxed tracking-[0.12em] text-[#D98A52]">{t(`cert.${c.id}.meta`, c.meta)}</p>
            <p className="mt-5 flex-1 text-base leading-relaxed text-[#A39E93]">{t(`cert.${c.id}.text`, c.text)}</p>
            {c.files.length ? (
              <div className="mt-8 flex flex-wrap gap-2">
                {c.files.map(([label, href], k) => (
                  <a key={href} href={href} download target="_blank" rel="noreferrer" className="btn-ghost w-fit" data-testid={`cert-download-${c.id}${k ? `-${k}` : ""}`}>
                    {t(`cert.file.${label}`, label)} <Download className="h-4 w-4" />
                  </a>
                ))}
              </div>
            ) : (
              <span className="btn-ghost mt-8 w-fit cursor-not-allowed opacity-60" data-testid={`cert-download-${c.id}`}>
                {t("cert.soon", "PDF in arrivo")}
              </span>
            )}
          </motion.article>
        ))}
      </div>
    </div>
  </section>
  );
};
